import { useMemo, useState } from "react";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gift, Search, X } from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { cn } from "@/lib/utils";

type ContentTypeKey = "meditation" | "breath" | "diet" | "rituel" | "frekans";

const CONTENT_TYPE_LABELS: Record<ContentTypeKey, string> = {
  meditation: "Meditasyon",
  breath: "Nefes",
  diet: "Diyet",
  rituel: "Ritüel",
  frekans: "Frekans",
};

type ContentPurchase = {
  id: string;
  user_id: string;
  content_type: string;
  content_id: string;
  price: number;
  purchased_at: string;
  order_id?: string | null;
  store_product_id?: string | null;
  source: "google_play" | "admin_gift" | string;
  user_email?: string | null;
  content_title?: string | null;
  karma_profiles?: { display_name?: string };
};

type SourceFilter = "all" | "google_play" | "admin_gift";

async function fetchContentPurchases(source: SourceFilter): Promise<ContentPurchase[]> {
  const qs = source !== "all" ? `?source=${encodeURIComponent(source)}` : "";
  const res = await adminFetch(`/api/admin/content-purchases${qs}`);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Satın almalar alınamadı.");
  return Array.isArray(json?.items) ? json.items : [];
}

async function giftContent(body: { userEmail: string; contentType: string; contentId: string }): Promise<void> {
  const res = await adminFetch("/api/admin/content-purchases/gift", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Hediye işlemi başarısız.");
}

type ContentOption = { id: string; title: string; price: number };

// Sadece ücretli (price > 0, store_product_id atanmış) içerikleri döndürür —
// ücretsiz içeriği hediye etmenin bir anlamı yok, zaten herkes erişebiliyor.
function isPaidItem(i: any): boolean {
  return Number(i?.price ?? 0) > 0;
}

async function fetchContentOptions(type: ContentTypeKey): Promise<ContentOption[]> {
  if (type === "meditation") {
    const res = await adminFetch("/api/admin/numeroloji/meditations");
    const json = (await res.json().catch(() => null)) as any;
    const items = Array.isArray(json?.items) ? json.items : [];
    return items.filter(isPaidItem).map((i: any) => ({ id: i.id, title: i.title, price: Number(i.price ?? 0) }));
  }
  if (type === "breath") {
    const res = await adminFetch("/api/admin/numeroloji/breaths");
    const json = (await res.json().catch(() => null)) as any;
    const items = Array.isArray(json?.items) ? json.items : [];
    return items.filter(isPaidItem).map((i: any) => ({ id: i.id, title: i.title, price: Number(i.price ?? 0) }));
  }
  if (type === "diet") {
    const res = await adminFetch("/api/admin/numeroloji/catalog");
    const json = (await res.json().catch(() => null)) as any;
    const items = Array.isArray(json?.diets) ? json.diets : [];
    return items.filter(isPaidItem).map((i: any) => ({ id: i.id, title: i.title, price: Number(i.price ?? 0) }));
  }
  if (type === "rituel") {
    const res = await adminFetch("/api/admin/numeroloji/catalog");
    const json = (await res.json().catch(() => null)) as any;
    const items = Array.isArray(json?.activities) ? json.activities : [];
    return items
      .filter((i: any) => Array.isArray(i.tags) && i.tags.includes("rituel"))
      .filter(isPaidItem)
      .map((i: any) => ({ id: i.id, title: i.title, price: Number(i.price ?? 0) }));
  }
  // frekans
  const res = await adminFetch("/api/admin/frekans-setleri");
  const json = (await res.json().catch(() => null)) as any;
  const items = Array.isArray(json?.data) ? json.data : [];
  return items.filter(isPaidItem).map((i: any) => ({ id: i.id, title: i.baslik, price: Number(i.price ?? 0) }));
}

function formatTry(n: number) {
  try {
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n);
  } catch {
    return `₺${n}`;
  }
}

function formatDate(iso: string) {
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function AdminIcerikSatinAlmalari() {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const { sessionQuery, loginMutation } = useAdminSession();
  const authenticated = Boolean(sessionQuery.data?.authenticated);
  const configured = Boolean(sessionQuery.data?.configured);
  const queryClient = useQueryClient();

  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [search, setSearch] = useState("");
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftEmail, setGiftEmail] = useState("");
  const [giftContentType, setGiftContentType] = useState<ContentTypeKey>("meditation");
  const [giftContentId, setGiftContentId] = useState("");
  const [giftError, setGiftError] = useState<string | null>(null);

  const purchasesQuery = useQuery({
    queryKey: ["admin-content-purchases-v2", sourceFilter],
    queryFn: () => fetchContentPurchases(sourceFilter),
    enabled: authenticated,
    staleTime: 15_000,
  });

  const contentOptionsQuery = useQuery({
    queryKey: ["admin-content-options", giftContentType],
    queryFn: () => fetchContentOptions(giftContentType),
    enabled: authenticated && showGiftModal,
    staleTime: 60_000,
  });

  const giftMutation = useMutation({
    mutationFn: giftContent,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-content-purchases-v2"] });
      setShowGiftModal(false);
      setGiftEmail("");
      setGiftContentId("");
      setGiftError(null);
    },
    onError: (err: Error) => setGiftError(err.message),
  });

  const items = purchasesQuery.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const email = (p.user_email ?? "").toLowerCase();
      const name = (p.karma_profiles?.display_name ?? "").toLowerCase();
      return email.includes(q) || name.includes(q);
    });
  }, [items, search]);

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.error?.message}
      />
    );
  }

  return (
    <div className="flex min-h-screen">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex-1 overflow-auto bg-slate-100/60 dark:bg-slate-900/30">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background/80 px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex items-center gap-3">
            <NumerolojiAdminMobileSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
            <h1 className="text-lg font-bold">İçerik Satın Almaları</h1>
          </div>
          <button
            type="button"
            onClick={() => { setShowGiftModal(true); setGiftError(null); }}
            className="flex items-center gap-2 rounded-full bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
          >
            <Gift className="h-4 w-4" />
            Hediye Et
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-2">
              {([
                { key: "all" as const, label: "Tümü" },
                { key: "google_play" as const, label: "Google Play" },
                { key: "admin_gift" as const, label: "Hediye" },
              ]).map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setSourceFilter(f.key)}
                  className={cn(
                    "rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors",
                    sourceFilter === f.key
                      ? "border-violet-500 bg-violet-500/20 text-violet-600 dark:text-violet-300"
                      : "border-slate-300 text-slate-500 hover:border-slate-400 dark:border-slate-700 dark:text-slate-400",
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="E-posta veya isimle ara…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 rounded-lg border border-slate-300 bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-slate-700"
              />
            </div>
          </div>

          {purchasesQuery.isLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Yükleniyor...</div>
          ) : purchasesQuery.isError ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {(purchasesQuery.error as Error).message}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Kayıt bulunamadı.</div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-xs uppercase tracking-wider text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3 text-left">Kullanıcı</th>
                    <th className="px-4 py-3 text-left">İçerik Tipi</th>
                    <th className="px-4 py-3 text-left">İçerik Başlığı</th>
                    <th className="px-4 py-3 text-left">Kaynak</th>
                    <th className="px-4 py-3 text-left">Tarih</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800/50">
                  {filtered.map((p) => (
                    <tr key={p.id} className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-3">
                        <div className="font-medium">{p.user_email ?? p.karma_profiles?.display_name ?? p.user_id.slice(0, 8) + "…"}</div>
                        {p.user_email && p.karma_profiles?.display_name && (
                          <div className="text-xs text-slate-400">{p.karma_profiles.display_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-slate-500/10 px-2.5 py-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
                          {CONTENT_TYPE_LABELS[p.content_type as ContentTypeKey] ?? p.content_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">{p.content_title ?? <span className="font-mono text-xs text-slate-400">{p.content_id}</span>}</td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "rounded-full px-2.5 py-0.5 text-xs font-semibold",
                            p.source === "admin_gift"
                              ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                              : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                          )}
                        >
                          {p.source === "admin_gift" ? "🎁 Hediye" : "Google Play"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{formatDate(p.purchased_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="text-xs text-slate-500">{filtered.length} kayıt gösteriliyor</div>
        </div>
      </div>

      {showGiftModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setGiftError(null);
              giftMutation.mutate({ userEmail: giftEmail.trim(), contentType: giftContentType, contentId: giftContentId });
            }}
            className="w-full max-w-md rounded-2xl border border-slate-200 bg-background p-6 shadow-xl dark:border-slate-700"
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 font-semibold">
                <Gift className="h-4 w-4 text-violet-500" />
                İçerik Hediye Et
              </div>
              <button type="button" onClick={() => setShowGiftModal(false)}>
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>

            <div className="grid gap-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Kullanıcı E-postası</label>
                <input
                  type="email"
                  required
                  value={giftEmail}
                  onChange={(e) => setGiftEmail(e.target.value)}
                  placeholder="kullanici@ornek.com"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-slate-700 dark:bg-slate-900"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">İçerik Tipi</label>
                <select
                  value={giftContentType}
                  onChange={(e) => { setGiftContentType(e.target.value as ContentTypeKey); setGiftContentId(""); }}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-slate-700 dark:bg-slate-900"
                >
                  {(Object.keys(CONTENT_TYPE_LABELS) as ContentTypeKey[]).map((key) => (
                    <option key={key} value={key}>{CONTENT_TYPE_LABELS[key]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">İçerik (sadece ücretli)</label>
                <select
                  required
                  value={giftContentId}
                  onChange={(e) => setGiftContentId(e.target.value)}
                  disabled={contentOptionsQuery.isLoading || (contentOptionsQuery.data ?? []).length === 0}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/30 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900"
                >
                  <option value="">
                    {contentOptionsQuery.isLoading
                      ? "Yükleniyor…"
                      : (contentOptionsQuery.data ?? []).length === 0
                        ? "Bu tipte ücretli içerik yok"
                        : "— İçerik seç —"}
                  </option>
                  {(contentOptionsQuery.data ?? []).map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.title} ({formatTry(opt.price)})</option>
                  ))}
                </select>
              </div>
            </div>

            {giftError && (
              <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {giftError}
              </div>
            )}

            <div className="mt-5 flex gap-2">
              <button
                type="submit"
                disabled={giftMutation.isPending || !giftEmail.trim() || !giftContentId}
                className="flex items-center gap-2 rounded-full bg-violet-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 hover:bg-violet-700"
              >
                <Gift className="h-4 w-4" />
                {giftMutation.isPending ? "Hediye Ediliyor…" : "Hediye Et"}
              </button>
              <button
                type="button"
                onClick={() => setShowGiftModal(false)}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
